import React, { useEffect } from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import USStateSelect, { getStateName } from '../shared/USStateSelect';
import ZIPCodeInput from '../shared/ZIPCodeInput';
import ChineseZIPCodeInput from '../shared/ChineseZIPCodeInput';
import EmailInput from '../shared/EmailInput';
import PhoneInput from '../shared/PhoneInput';
import ProvinceCitySelect from '../shared/ProvinceCitySelect';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2, Calendar, MapPin, Plane, CreditCard, User, ChevronDown } from 'lucide-react';

// 旅行信息表单
const TravelForm = ({ data, onChange, fieldErrors = {} }) => {

  // 从formDataStorage读取数据并确保数据同步
  useEffect(() => {
    const savedData = formDataStorage.loadFormData('travel');
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('TravelForm useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
    
    // 确保关键字段存在
    if (data.has_specific_plan === undefined) {
      onChange('has_specific_plan', false);
    }
    
    // 确保至少有一个目的选项
    const currentPurpose = (savedData && savedData.purpose) || data.purpose || [];
    if (currentPurpose.length === 0) {
      onChange('purpose', [{ main_type: '', sub_type: '' }]);
    }
  }, [data, onChange]);
  
  // 邮政编码验证函数
  const validateZipCode = (zipCode) => {
    if (!zipCode) return null;
    if (!/^\d{6}$/.test(zipCode)) {
      return '邮政编码格式不正确（应为6位数字）';
    }
    return null;
  };
  
  // 监听邮政编码变化并验证
  useEffect(() => {
    if (data.payer_country === 'CHIN') {
      // 验证资助人地址邮政编码
      const payerZipError = validateZipCode(data.payer_zip_code);
      if (payerZipError && data.payer_zip_code) {
        console.log('资助人邮政编码验证错误:', payerZipError);
      }
      
      // 验证其他公司/组织支付地址邮政编码
      const otherPayerZipError = validateZipCode(data.payer_zip_code);
      if (otherPayerZipError && data.payer_zip_code) {
        console.log('其他支付地址邮政编码验证错误:', otherPayerZipError);
      }
    }
  }, [data.payer_zip_code, data.payer_country]);
  
  const VISA_TYPES = {
    'B': {
      name: '临时商务或观光访客',
      subTypes: [
        { value: 'B1-B2', label: '商务或观光（临时访客）' },
        { value: 'B1-CF', label: '商务/会议' },
        { value: 'B2-TM', label: '观光/医疗' },
      ]
    },
    'F': {
      name: '学术或语言学生',
      subTypes: [
        { value: 'F1-F1', label: '学生' },
        { value: 'F2-CH', label: 'F1签证持有者的子女' },
        { value: 'F2-SP', label: 'F1签证持有者的配偶' }
      ]
    },
  };

  // 月份映射
  const MONTHS = [
    { value: '1', label: '01月 (JAN)' },
    { value: '2', label: '02月 (FEB)' },
    { value: '3', label: '03月 (MAR)' },
    { value: '4', label: '04月 (APR)' },
    { value: '5', label: '05月 (MAY)' },
    { value: '6', label: '06月 (JUN)' },
    { value: '7', label: '07月 (JUL)' },
    { value: '8', label: '08月 (AUG)' },
    { value: '9', label: '09月 (SEP)' },
    { value: '10', label: '10月 (OCT)' },
    { value: '11', label: '11月 (NOV)' },
    { value: '12', label: '12月 (DEC)' }
  ];



  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1);
      return { value: day, label: `${day}日` };
    });
  };

  // 更新旅行目的
  const updatePurpose = (index, field, value) => {
    const currentPurpose = data.purpose || [{ main_type: '', sub_type: '' }];
    const newPurpose = [...currentPurpose];
    if (!newPurpose[index]) {
      newPurpose[index] = { main_type: '', sub_type: '' };
    }

    // 如果改变了主要类型，清除子类型
    if (field === 'main_type') {
      newPurpose[index] = {
        ...newPurpose[index],
        [field]: value,
        sub_type: '' // 清除子类型
      };
    } else {
      newPurpose[index] = { ...newPurpose[index], [field]: value };
    }

    onChange('purpose', newPurpose);
  };

  // 添加旅行目的
  const addPurpose = () => {
    const currentPurpose = data.purpose || [];
    const newPurpose = [...currentPurpose, { main_type: '', sub_type: '' }];
    onChange('purpose', newPurpose);
  };

  // 删除旅行目的
  const removePurpose = (index) => {
    const currentPurpose = data.purpose || [];
    // 确保至少保留一个目的选项
    if (currentPurpose.length > 1) {
      const newPurpose = currentPurpose.filter((_, i) => i !== index);
      onChange('purpose', newPurpose);
    }
  };

  // 更新访问地点
  const updateLocation = (index, value) => {
    const currentLocations = data.location || [];
    const newLocations = [...currentLocations];
    newLocations[index] = value;
    onChange('location', newLocations);
  };

  // 添加访问地点
  const addLocation = () => {
    const currentLocations = data.location || [];
    const newLocations = [...currentLocations, ''];
    onChange('location', newLocations);
  };

  // 删除访问地点
  const removeLocation = (index) => {
    const currentLocations = data.location || [];
    const newLocations = currentLocations.filter((_, i) => i !== index);
    onChange('location', newLocations);
  };

  // 日期验证函数
  const validateArrivalDate = () => {
    if (!data.arrival_year || !data.arrival_month || !data.arrival_day) {
      return null;
    }
    
    const arrivalDate = new Date(data.arrival_year, data.arrival_month - 1, data.arrival_day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (arrivalDate <= today) {
      return '到达日期必须在今天之后';
    }
    return null;
  };

  const validateDepartureDate = () => {
    if (!data.has_specific_plan || !data.departure_year || !data.departure_month || !data.departure_day) {
      return null;
    }
    
    const departureDate = new Date(data.departure_year, data.departure_month - 1, data.departure_day);
    
    // 如果到达日期也有效，验证日期顺序
    if (data.arrival_year && data.arrival_month && data.arrival_day) {
      const arrivalDate = new Date(data.arrival_year, data.arrival_month - 1, data.arrival_day);
      
      if (departureDate <= arrivalDate) {
        return '离开日期必须在到达日期之后';
      }
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">✈</span>
          旅行信息
        </h3>

        {/* 旅行目的 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                赴美访问目的 (Purpose of Trip to the U.S.)
              </h4>
              <p className="text-sm text-gray-600 mt-1">以下选项来自美国官方签证申请表，请根据您的实际情况选择</p>
            </div>
            <button
              type="button"
              onClick={addPurpose}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
            >
              <Plus className="w-4 h-4 mr-1" />
              添加目的
            </button>
          </div>

          <div className="space-y-4">
            {(() => {
              const purposes = data.purpose || [{ main_type: '', sub_type: '' }];
              return purposes.map((purpose, index) => (
                <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg border-l-4 border-purple-400 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 space-y-4">

                      {/* 主要签证类型选择 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            签证主要类型 {index + 1} <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={purpose.main_type || ''}
                              onChange={(e) => updatePurpose(index, 'main_type', e.target.value)}
                              className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                            >
                              <option value="">请选择主要类型...</option>
                              {Object.entries(VISA_TYPES).map(([key, type]) => (
                                <option key={key} value={key}>
                                  {key} - {type.name}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        </div>

                        {/* 子类型选择 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            具体签证类型 <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={purpose.sub_type || ''}
                              onChange={(e) => updatePurpose(index, 'sub_type', e.target.value)}
                              disabled={!purpose.main_type}
                              className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">
                                {purpose.main_type ? '请选择具体类型...' : '请先选择主要类型'}
                              </option>
                              {purpose.main_type && VISA_TYPES[purpose.main_type] &&
                                VISA_TYPES[purpose.main_type].subTypes.map(subType => (
                                  <option key={subType.value} value={subType.value}>
                                    {subType.label}
                                  </option>
                                ))
                              }
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 当前选择预览 */}
                      {purpose.main_type && purpose.sub_type && (
                        <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
                          <div className="text-sm font-medium text-green-800">
                            ✅ 已选择: {purpose.sub_type}
                          </div>
                          <div className="text-xs text-green-600 mt-1">
                            {VISA_TYPES[purpose.main_type]?.subTypes.find(st => st.value === purpose.sub_type)?.label}
                          </div>
                        </div>
                      )}

                      {/* F2家属签证主申请人信息 */}
                      {purpose.sub_type && (purpose.sub_type === 'F2-CH' || purpose.sub_type === 'F2-SP') && (
                        <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-400">
                          <h5 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                            <User className="w-4 h-4 mr-2 text-orange-600" />
                            主申请人信息 (Principal Applicant Information)
                          </h5>
                          <p className="text-sm text-orange-700 mb-4">
                            {purpose.sub_type === 'F2-CH' 
                              ? '作为F1学生的子女，请提供F1主申请人（您的父亲或母亲）的信息：'
                              : '作为F1学生的配偶，请提供F1主申请人（您的配偶）的信息：'
                            }
                          </p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                主申请人姓氏 (Principal Applicant Surnames) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={data.principal_applicant_surnames || ''}
                                onChange={(e) => {
                                  const value = e.target.value.slice(0, 33); // 限制33字符
                                  onChange('principal_applicant_surnames', value);
                                }}
                                maxLength={33}
                                className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-gradient-to-r from-white to-orange-50 shadow-sm hover:shadow-md transition-all duration-200"
                                placeholder="请输入主申请人的姓氏"
                                required
                              />
                              <div className="mt-1 text-xs text-gray-500">
                                字符数：{(data.principal_applicant_surnames || '').length}/33
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                主申请人名字 (Principal Applicant Given Names) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={data.principal_applicant_given_name || ''}
                                onChange={(e) => {
                                  const value = e.target.value.slice(0, 33); // 限制33字符
                                  onChange('principal_applicant_given_name', value);
                                }}
                                maxLength={33}
                                className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-gradient-to-r from-white to-orange-50 shadow-sm hover:shadow-md transition-all duration-200"
                                placeholder="请输入主申请人的名字"
                                required
                              />
                              <div className="mt-1 text-xs text-gray-500">
                                字符数：{(data.principal_applicant_given_name || '').length}/33
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 p-3 bg-orange-100 rounded-lg">
                            <div className="text-xs text-orange-600">
                              <strong>注意：</strong> 
                              请确保姓名与F1主申请人的护照和签证文件完全一致。姓氏和名字分别不能超过33个字符。
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                    {purposes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePurpose(index)}
                        className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                        title="删除此目的"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* 具体计划 */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              您是否有具体的旅行计划？ <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="has_specific_plan"
                  value="false"
                  checked={(data.has_specific_plan !== undefined ? data.has_specific_plan : false) === false}
                  onChange={(e) => {
                    onChange('has_specific_plan', false);
                    onChange('location', []);
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 我没有具体计划</span>
              </label>
              
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                <input
                  type="radio"
                  name="has_specific_plan"
                  value="true"
                  checked={(data.has_specific_plan !== undefined ? data.has_specific_plan : false) === true}
                  onChange={(e) => {
                    onChange('has_specific_plan', true);
                    addLocation();
                  }}
                  className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 我有具体计划</span>
              </label>
            </div>
          </div>

          {/* 具体计划提示 */}
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 flex items-start">
              <span className="mr-2">💡</span>
              <span>填写建议：建议选择"有具体计划"，写明3-4个城市或景点。旅游签建议7-14天，超过30天可能被质疑</span>
            </p>
          </div>
        </div>

        {/* 到达信息 - 根据是否有具体计划决定显示内容 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Plane className="w-5 h-5 mr-2" />
            {data.has_specific_plan ? '到达信息' : '访美时间'}
          </h4>
          <p className="text-xs text-amber-600 mb-4">提示：提交前请记住您填写的抵达城市、日期、停留天数，面签时大概率会被问到，回答要与表格一致。</p>

          {/* 到达日期 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {data.has_specific_plan ? '到达日期' : '计划访美时间'} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                <input
                  id="travel_arrival_year"
                  type="number"
                  min="2024"
                  max="2030"
                  placeholder="2026"
                  value={data.arrival_year || ''}
                  onChange={(e) => onChange('arrival_year', e.target.value)}
                  className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                <div className="relative">
                  <select
                    id="travel_arrival_month"
                    value={data.arrival_month || ''}
                    onChange={(e) => onChange('arrival_month', e.target.value)}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                  >
                    <option value="">选择月份</option>
                    {MONTHS.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                <div className="relative">
                  <select
                    id="travel_arrival_day"
                    value={data.arrival_day || ''}
                    onChange={(e) => onChange('arrival_day', e.target.value)}
                    disabled={!data.arrival_year || !data.arrival_month}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <option value="">选择日期</option>
                    {generateDayOptions(data.arrival_year, data.arrival_month).map(day => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 到达日期验证错误 */}
            {validateArrivalDate() && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center">
                  <span className="w-4 h-4 mr-1">⚠️</span>
                  {validateArrivalDate()}
                </p>
              </div>
            )}
            {fieldErrors && fieldErrors['#travel_arrival_year'] && (
              <div className="mt-1 text-xs text-red-600">{fieldErrors['#travel_arrival_year']}</div>
            )}
          </div>

          {/* 详细到达信息 - 只在有具体计划时显示 */}
          {data.has_specific_plan && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="到达航班号"
                value={data.arrival_flight || ''}
                onChange={(e) => onChange('arrival_flight', e.target.value)}
                placeholder="CA123"
              />
              <FormField
                label="到达城市"
                value={data.arrival_city || ''}
                onChange={(e) => onChange('arrival_city', e.target.value)}
                placeholder="LOS ANGELES"
              />
            </div>
          )}
        </div>

        {/* 停留时间 */}
        {!data.has_specific_plan && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              停留信息
            </h4>
            <p className="text-xs text-amber-600 mb-4">提示：一般旅游建议填写 7-14 天，时间太长（如 30-90 天）容易被追问资金和国内约束力。</p>

            {/* 停留时间警告提示 */}
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
              <p className="text-sm text-orange-800 flex items-start">
                <span className="mr-2">⚠️</span>
                <span>重要提示：旅游签建议7-14天，超过30天可能被质疑</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <FormField
                label="停留天数"
                type="number"
                value={data.stay_duration || ''}
                onChange={(e) => onChange('stay_duration', e.target.value)}
                placeholder="15"
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  时间单位 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={data.stay_duration_unit || ''}
                    onChange={(e) => onChange('stay_duration_unit', e.target.value)}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">请选择...</option>
                    <option value="D">天 (Days)</option>
                    <option value="W">周 (Weeks)</option>
                    <option value="M">月 (Months)</option>
                    <option value="Y">年 (Years)</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>)
        }

        {/* 离开信息 - 只在有具体计划时显示 */}
        {data.has_specific_plan && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Plane className="w-5 h-5 mr-2 transform rotate-45" />
              离开信息
            </h4>

            {/* 离开日期 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                离开日期
              </label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                  <input
                    id="travel_departure_year"
                    type="number"
                    min="2024"
                    max="2030"
                    placeholder="2026"
                    value={data.departure_year || ''}
                    onChange={(e) => onChange('departure_year', e.target.value)}
                    className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                  <div className="relative">
                    <select
                      id="travel_departure_month"
                      value={data.departure_month || ''}
                      onChange={(e) => onChange('departure_month', e.target.value)}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                    >
                      <option value="">选择月份</option>
                      {MONTHS.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                  <div className="relative">
                    <select
                      id="travel_departure_day"
                      value={data.departure_day || ''}
                      onChange={(e) => onChange('departure_day', e.target.value)}
                      disabled={!data.departure_year || !data.departure_month}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <option value="">选择日期</option>
                      {generateDayOptions(data.departure_year, data.departure_month).map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 离开日期验证错误 */}
              {validateDepartureDate() && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600 flex items-center">
                    <span className="w-4 h-4 mr-1">⚠️</span>
                    {validateDepartureDate()}
                  </p>
                </div>
              )}
              {fieldErrors && fieldErrors['#travel_departure_year'] && (
                <div className="mt-1 text-xs text-red-600">{fieldErrors['#travel_departure_year']}</div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="离开航班号"
                value={data.departure_flight || ''}
                onChange={(e) => onChange('departure_flight', e.target.value)}
                placeholder="CA456"
              />
              <FormField
                label="离开城市"
                value={data.departure_city || ''}
                onChange={(e) => onChange('departure_city', e.target.value)}
                placeholder="LOS ANGELES"
              />
            </div>
          </div>
        )}

        {/* 访问地点 - 只在有具体计划时显示 */}
        {data.has_specific_plan && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">计划访问的地点</h4>
              <button
                type="button"
                onClick={addLocation}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <Plus className="w-4 h-4 mr-1" />
                添加地点
              </button>
            </div>

            <div className="space-y-3">
              {(() => {
                return data.location.map((location, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-green-50 rounded-lg border-l-4 border-green-400 shadow-sm">
                    <div className="flex-1">
                      <FormField
                        label={`地点 ${index + 1}`}
                        value={location || ''}
                        onChange={(e) => updateLocation(index, e.target.value)}
                        placeholder="LOS ANGELES"
                      />
                    </div>
                    {data.location.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLocation(index)}
                        className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                        title="删除此地点"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* 资助信息 - 一直显示 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <CreditCard className="w-5 h-5 mr-2" />
            资助信息
          </h4>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              谁来支付您此次旅行费用？ <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={data.finance || ''}
                onChange={(e) => onChange('finance', e.target.value)}
                className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
              >
                <option value="">请选择...</option>
                <option value="S">自己 (Self)</option>
                <option value="O">其他人 (Other Person)</option>
                <option value="P">现在的雇主 (Present Employer)</option>
                <option value="U">美国的雇主 (Employer in the U.S.)</option>
                <option value="C">其他公司/组织 (Other Company/Organization)</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          {/* 其他人支付时的详细信息 */}
          {data.finance === 'O' && (
            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-4 border-blue-400">
              <h5 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <User className="w-4 h-4 mr-2" />
                付费人信息
              </h5>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FormField
                  label="付费人姓氏"
                  value={data.surnames || ''}
                  onChange={(e) => onChange('surnames', e.target.value)}
                  placeholder="ZHANG"
                />
                <FormField
                  label="付费人名字"
                  value={data.given_name || ''}
                  onChange={(e) => onChange('given_name', e.target.value)}
                  placeholder="SAN"
                />
                <PhoneInput
                  label="电话号码"
                  value={data.telephone_number || ''}
                  onChange={(value) => onChange('telephone_number', value)}
                  placeholder="13800138000"
                />
                <div id="payer_email" />
                <EmailInput
                  label="电子邮箱"
                  value={data.email || ''}
                  onChange={(value) => onChange('email', value)}
                  placeholder="test@example.com"
                />
                {fieldErrors && fieldErrors['#payer_email'] && (
                  <div className="-mt-2 text-xs text-red-600">{fieldErrors['#payer_email']}</div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  与您的关系
                </label>
                <div className="relative">
                  <select
                    value={data.relationship || ''}
                    onChange={(e) => onChange('relationship', e.target.value)}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="">请选择...</option>
                    <option value="P">父母 (Parent)</option>
                    <option value="S">配偶 (Spouse)</option>
                    <option value="C">子女 (Child)</option>
                    <option value="R">亲戚 (Relative)</option>
                    <option value="F">朋友 (Friend)</option>
                    <option value="E">雇主 (Employer)</option>
                    <option value="O">其他 (Other)</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* 地址信息 */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={data.same_address || false}
                    onChange={(e) => onChange('same_address', e.target.checked)}
                    className="mr-3 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-sm font-medium text-gray-700">为您支付旅行费用的一方，其地址是否与您的家庭地址或邮寄地址相同？</span>
                </label>
              </div>

              {!data.same_address && (
                <div className="space-y-4">
                  <FormField
                    label="地址行 1"
                    value={data.payer_address || ''}
                    onChange={(e) => onChange('payer_address', e.target.value)}
                    placeholder="114"
                  />
                  <FormField
                    label="地址行 2(可选)"
                    value={data.payer_address2 || ''}
                    onChange={(e) => onChange('payer_address2', e.target.value)}
                    placeholder="514"
                  />
                  {/* 根据国家选择显示不同的省份城市组件 */}
                  {['CHIN', 'HNK', 'TWAN', 'MAC'].includes(data.payer_country) ? (
                    <ProvinceCitySelect
                      provinceValue={data.payer_province || ''}
                      cityValue={data.payer_city || ''}
                      onProvinceChange={(value) => {
                        onChange('payer_province', value);
                        onChange('payer_city', ''); // 清空城市选择
                      }}
                      onCityChange={(value) => onChange('payer_city', value)}
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <FormField
                        label="城市"
                        value={data.payer_city || ''}
                        onChange={(e) => onChange('payer_city', e.target.value)}
                        placeholder="TEST CITY"
                      />
                      <FormField
                        label="省份/州"
                        value={data.payer_province || ''}
                        onChange={(e) => onChange('payer_province', e.target.value)}
                        placeholder="California"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CountrySelect
                      label="国家/地区"
                      value={data.payer_country || ''}
                      onChange={(value) => onChange('payer_country', value)}
                    />
                    {/* 根据国家选择显示不同的邮政编码组件 */}
                    <div>
                    {data.payer_country === 'CHIN' ? (
                      <>
                        <div id="payer_zip_code" />
                        <ChineseZIPCodeInput
                          label="邮政编码"
                          value={data.payer_zip_code || ''}
                          onChange={(value) => onChange('payer_zip_code', value)}
                          countryCode={data.payer_country}
                        />
                        {fieldErrors && fieldErrors['#payer_zip_code'] && (
                          <div className="-mt-2 text-xs text-red-600">{fieldErrors['#payer_zip_code']}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div id="payer_zip_code" />
                        <ZIPCodeInput
                          label="邮政编码"
                          value={data.payer_zip_code || ''}
                          onChange={(value) => onChange('payer_zip_code', value)}
                        />
                        {fieldErrors && fieldErrors['#payer_zip_code'] && (
                          <div className="-mt-2 text-xs text-red-600">{fieldErrors['#payer_zip_code']}</div>
                        )}
                      </>
                    )}
                      {/* 显示邮政编码验证错误 */}
                      {data.payer_country === 'CHIN' && data.payer_zip_code && validateZipCode(data.payer_zip_code) && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-600 flex items-center">
                            <span className="w-4 h-4 mr-1">⚠️</span>
                            {validateZipCode(data.payer_zip_code)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 现在的雇主支付时的信息 */}
          {data.finance === 'P' && (
            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-4 border-blue-400">
              <h5 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <User className="w-4 h-4 mr-2" />
                现在的雇主支付
              </h5>

              <div className="text-center p-6">
                <div className="text-blue-400 mb-4">
                  <User className="w-16 h-16 mx-auto" />
                </div>
                <h6 className="text-lg font-semibold text-gray-800 mb-2">现在的雇主支付</h6>
                <p className="text-gray-600 mb-4">
                  您选择了由现在的雇主支付此次旅行费用。
                </p>
                <div className="bg-blue-100 p-4 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>支付方式:</strong> 现在的雇主<br />
                    <strong>说明:</strong> 无需填写额外的雇主详细信息
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 美国的雇主支付时的信息 */}
          {data.finance === 'U' && (
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-l-4 border-indigo-400">
              <h5 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <User className="w-4 h-4 mr-2" />
                美国雇主支付
              </h5>

              <div className="text-center p-6">
                <div className="text-indigo-400 mb-4">
                  <User className="w-16 h-16 mx-auto" />
                </div>
                <h6 className="text-lg font-semibold text-gray-800 mb-2">美国雇主支付</h6>
                <p className="text-gray-600 mb-4">
                  您选择了由美国雇主支付此次旅行费用。
                </p>
                <div className="bg-indigo-100 p-4 rounded-lg">
                  <p className="text-sm text-indigo-700">
                    <strong>支付方式:</strong> 美国雇主<br />
                    <strong>国家/地区:</strong> 🇺🇸 美国<br />
                    <strong>说明:</strong> 无需填写额外的雇主详细信息
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 其他公司/组织支付时的信息 */}
          {data.finance === 'C' && (
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
              <h5 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <User className="w-4 h-4 mr-2" />
                其他公司/组织信息
              </h5>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FormField
                  label="公司/组织名称"
                  value={data.company_name || ''}
                  onChange={(e) => onChange('company_name', e.target.value)}
                  placeholder="TEST ORGANIZATION"
                />
                <PhoneInput
                  label="电话号码"
                  value={data.telephone_number || ''}
                  onChange={(value) => onChange('telephone_number', value)}
                  placeholder="13800138000"
                />
              </div>

              <div className="mb-4">
                <FormField
                  label="与组织的关系"
                  value={data.relationship || ''}
                  onChange={(e) => onChange('relationship', e.target.value)}
                  placeholder="赞助商/合作伙伴/等"
                />
              </div>

              {/* 组织地址信息 */}
              <div className="space-y-4">
                <h6 className="text-sm font-semibold text-gray-700">组织地址</h6>
                <FormField
                  label="地址行 1"
                  value={data.payer_address || ''}
                  onChange={(e) => onChange('payer_address', e.target.value)}
                  placeholder="114"
                />
                <FormField
                  label="地址行 2"
                  value={data.payer_address2 || ''}
                  onChange={(e) => onChange('payer_address2', e.target.value)}
                  placeholder="514"
                />
                {/* 根据国家选择显示不同的省份城市组件 */}
                {['CHIN', 'HNK', 'TWAN', 'MAC'].includes(data.payer_country) ? (
                  <ProvinceCitySelect
                    provinceValue={data.payer_province || ''}
                    cityValue={data.payer_city || ''}
                    onProvinceChange={(value) => {
                      onChange('payer_province', value);
                      onChange('payer_city', ''); // 清空城市选择
                    }}
                    onCityChange={(value) => onChange('payer_city', value)}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <FormField
                      label="城市"
                      value={data.payer_city || ''}
                      onChange={(e) => onChange('payer_city', e.target.value)}
                      placeholder="TEST CITY"
                    />
                    <FormField
                      label="省份/州"
                      value={data.payer_province || ''}
                      onChange={(e) => onChange('payer_province', e.target.value)}
                      placeholder="California"
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CountrySelect
                    label="国家/地区"
                    value={data.payer_country || ''}
                    onChange={(value) => onChange('payer_country', value)}
                  />
                  {/* 根据国家选择显示不同的邮政编码组件 */}
                  <div>
                    {data.payer_country === 'CHIN' ? (
                      <ChineseZIPCodeInput
                        label="邮政编码"
                        value={data.payer_zip_code || ''}
                        onChange={(value) => onChange('payer_zip_code', value)}
                        countryCode={data.payer_country}
                      />
                    ) : (
                      <ZIPCodeInput
                        label="邮政编码"
                        value={data.payer_zip_code || ''}
                        onChange={(value) => onChange('payer_zip_code', value)}
                      />
                    )}
                    {/* 显示邮政编码验证错误 */}
                    {data.payer_country === 'CHIN' && data.payer_zip_code && validateZipCode(data.payer_zip_code) && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600 flex items-center">
                          <span className="w-4 h-4 mr-1">⚠️</span>
                          {validateZipCode(data.payer_zip_code)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 美国地址信息 - 常驻必填模块 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            美国地址信息
          </h4>
          <p className="text-sm text-gray-600 mb-6">请提供您在美国的地址信息（基于DS-160表单要求）</p>

          <div className="space-y-4">
            {/* 街道地址第一行 */}
            <FormField
              label="街道地址（第一行）"
              value={data.us_street_address1 || ''}
              onChange={(e) => onChange('us_street_address1', e.target.value)}
              placeholder="123 Main Street"
              required
              maxLength={40}
            />

            {/* 街道地址第二行 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                街道地址（第二行）<span className="text-gray-500 italic ml-1">*可选</span>
              </label>
              <input
                type="text"
                value={data.us_street_address2 || ''}
                onChange={(e) => onChange('us_street_address2', e.target.value)}
                placeholder="Apt 456"
                maxLength={40}
                className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200"
              />
              <p className="text-xs text-gray-500 mt-1">最多40个字符</p>
            </div>

            {/* 城市和州 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="城市"
                value={data.us_city || ''}
                onChange={(e) => onChange('us_city', e.target.value)}
                placeholder="LOS ANGELES"
                required
                maxLength={20}
              />

              <div id="us_addr_state" />
              <USStateSelect
                label="州"
                value={data.us_state || ''}
                onChange={(value) => onChange('us_state', value)}
                required
              />
            </div>

            {/* 邮政编码 */}
            <div id="us_addr_zip" />
            <ZIPCodeInput
              label="邮政编码"
              value={data.us_zip_code || ''}
              onChange={(value) => onChange('us_zip_code', value)}
              placeholder="12345 或 12345-6789"
              className="md:w-2/3"
            />

            {/* 地址预览 */}
            {(data.us_street_address1 || data.us_city || data.us_state) && (
              <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
                <h5 className="text-sm font-semibold text-blue-800 mb-2">美国地址预览</h5>
                <div className="text-sm text-blue-700">
                  {data.us_street_address1 && <div>{data.us_street_address1}</div>}
                  {data.us_street_address2 && <div>{data.us_street_address2}</div>}
                  <div>
                    {data.us_city && <span>{data.us_city}</span>}
                    {data.us_city && data.us_state && <span>, </span>}
                    {data.us_state && <span>{getStateName(data.us_state)}</span>}
                    {data.us_zip_code && <span> {data.us_zip_code}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 预览信息 */}
        {(data.purpose || data.arrival_city) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400 shadow-sm">
            <h4 className="font-semibold text-purple-800 mb-2">信息预览</h4>
            <div className="text-sm text-purple-700 space-y-1">
              {data.purpose && data.purpose.length > 0 && (
                <p><strong>签证类型:</strong> {data.purpose.map(p => {
                  if (p.sub_type) {
                    // 显示具体的子类型
                    return p.sub_type;
                  } else if (p.main_type && VISA_TYPES[p.main_type]) {
                    // 只选择了主类型
                    return `${p.main_type} - ${VISA_TYPES[p.main_type].name}`;
                  }
                  return '未完成选择';
                }).filter(Boolean).join(', ')}</p>
              )}
              <p><strong>具体计划:</strong> {data.has_specific_plan ? '✅ 有具体计划' : '❌ 无具体计划'}</p>
              {data.arrival_year && data.arrival_month && data.arrival_day && (
                <p>
                  <strong>{data.has_specific_plan ? '到达日期' : '访美时间'}:</strong> {data.arrival_year}-{data.arrival_month}-{data.arrival_day}
                  {validateArrivalDate() && (
                    <span className="ml-2 text-red-600 text-xs">⚠️ {validateArrivalDate()}</span>
                  )}
                </p>
              )}
              {data.stay_duration && data.stay_duration_unit && (
                <p><strong>停留时间:</strong> {data.stay_duration} {
                  { 'D': '天', 'W': '周', 'M': '月', 'Y': '年' }[data.stay_duration_unit]
                }</p>
              )}

              {/* 详细信息 - 只在有具体计划时显示 */}
              {data.has_specific_plan && (
                <>
                  {data.arrival_city && <p><strong>到达城市:</strong> {data.arrival_city}</p>}
                  {data.departure_year && data.departure_month && data.departure_day && (
                    <p>
                      <strong>离开日期:</strong> {data.departure_year}-{data.departure_month}-{data.departure_day}
                      {validateDepartureDate() && (
                        <span className="ml-2 text-red-600 text-xs">⚠️ {validateDepartureDate()}</span>
                      )}
                    </p>
                  )}
                  {data.location && data.location.length > 0 && (
                    <p><strong>访问地点:</strong> {data.location.filter(l => l).join(', ')}</p>
                  )}
                </>
              )}

              {/* 资助信息*/}
              {data.finance && (
                <p><strong>费用支付:</strong> {
                  {
                    'S': '自己',
                    'O': '其他人',
                    'P': '现在的雇主',
                    'U': '美国的雇主',
                    'C': '其他公司/组织'
                  }[data.finance]
                }</p>
              )}
              {data.finance === 'O' && data.surnames && data.given_name && (
                <p><strong>付费人:</strong> {data.surnames} {data.given_name}</p>
              )}
              {data.finance === 'C' && data.company_name && (
                <p><strong>支付机构:</strong> {data.company_name}</p>
              )}
              {data.finance === 'P' && (
                <p><strong>支付方式:</strong> 现在的雇主</p>
              )}
              {data.finance === 'U' && (
                <p><strong>支付方式:</strong> 美国雇主 (🇺🇸 美国)</p>
              )}
              {(data.finance === 'O' || data.finance === 'C') && data.payer_country && (
                <p><strong>付费方地址:</strong> {data.payer_city}{data.payer_city && ', '}{getCountryName(data.payer_country)}</p>
              )}

              {/* 美国地址信息 */}
              {(data.us_street_address1 || data.us_city || data.us_state) && (
                <p><strong>美国地址:</strong> {[
                  data.us_street_address1,
                  data.us_street_address2,
                  [data.us_city, data.us_state ? getStateName(data.us_state) : null, data.us_zip_code].filter(Boolean).join(', ')
                ].filter(Boolean).join(', ')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelForm;
